import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { API_URL } from "../api";
import { formatAed, formatBookingDateTime } from "../utils/booking";
import { PENDING_PAYMENT_KEY } from "./Booking";

const MAX_STATUS_CHECKS = 8;
const STATUS_CHECK_DELAY_MS = 3000;

function getStoredOrderRef() {
  try {
    return sessionStorage.getItem(PENDING_PAYMENT_KEY) || "";
  } catch (error) {
    return "";
  }
}

function PaymentResult() {
  const [searchParams] = useSearchParams();
  const orderRef = searchParams.get("ref") || getStoredOrderRef();
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState("");
  const [checks, setChecks] = useState(0);

  useEffect(() => {
    if (!orderRef) return undefined;

    let timeout;
    let cancelled = false;

    fetch(`${API_URL}/payments/status/${encodeURIComponent(orderRef)}`, {
      cache: "no-store",
    })
      .then(async (response) => {
        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || "Could not check your payment.");
        }
        if (cancelled) return;

        setBooking(data);
        setError("");
        if (data.status === "paid") {
          try {
            sessionStorage.removeItem(PENDING_PAYMENT_KEY);
          } catch (storageError) {
            // Nothing to clean up.
          }
        }
        if (data.status === "pending" && checks < MAX_STATUS_CHECKS) {
          timeout = setTimeout(
            () => setChecks((count) => count + 1),
            STATUS_CHECK_DELAY_MS,
          );
        }
      })
      .catch((statusError) => {
        if (!cancelled) setError(statusError.message);
      });

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [orderRef, checks]);

  if (!orderRef) {
    return (
      <main className="bookingPage">
        <section className="bookingNotice">
          <h1>No payment found</h1>
          <p>We could not find a payment to check.</p>
          <Link to="/" className="bookingBackLink">
            Back to cars
          </Link>
        </section>
      </main>
    );
  }

  if (error && !booking) {
    return (
      <main className="bookingPage">
        <section className="bookingNotice">
          <h1>We could not check your payment</h1>
          <p>{error}</p>
          <p>
            If money was taken from your card, please contact us and we will
            sort it out.
          </p>
          <Link to="/contact" className="bookingBackLink">
            Contact us
          </Link>
        </section>
      </main>
    );
  }

  if (!booking) {
    return (
      <main className="bookingPage">
        <p className="bookingLoading">Checking your payment...</p>
      </main>
    );
  }

  const carName = [booking.car?.companyName, booking.car?.name]
    .filter(Boolean)
    .join(" ");

  if (booking.status === "paid") {
    return (
      <main className="bookingPage">
        <section className="bookingNotice bookingNoticeSuccess">
          <p className="bookingNoticeEyebrow">Payment successful</p>
          <h1>Thank you, {booking.customer.firstName}! Your booking is confirmed.</h1>
          <p>
            A confirmation email is on its way to{" "}
            <strong>{booking.customer.email}</strong>.
          </p>
          <dl className="bookingDetails">
            <div>
              <dt>Booking reference</dt>
              <dd>{booking.reference}</dd>
            </div>
            <div>
              <dt>Car</dt>
              <dd>{carName} or similar</dd>
            </div>
            <div>
              <dt>Pickup</dt>
              <dd>{formatBookingDateTime(booking.pickupAt)}</dd>
            </div>
            <div>
              <dt>Return</dt>
              <dd>{formatBookingDateTime(booking.returnAt)}</dd>
            </div>
            <div className="bookingTotal">
              <dt>Total paid</dt>
              <dd>{formatAed(booking.pricing.total)}</dd>
            </div>
          </dl>
          <Link to="/" className="bookingBackLink">
            Back to home
          </Link>
        </section>
      </main>
    );
  }

  if (booking.status === "failed" || booking.status === "expired") {
    return (
      <main className="bookingPage">
        <section className="bookingNotice bookingNoticeFailed">
          <p className="bookingNoticeEyebrow">Payment not completed</p>
          <h1>Your payment did not go through</h1>
          <p>
            No booking was made. You can try again with another card or book
            with us on WhatsApp.
          </p>
          <Link to="/" className="bookingBackLink">
            Back to cars
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="bookingPage">
      <section className="bookingNotice">
        <p className="bookingNoticeEyebrow">Payment pending</p>
        <h1>We are waiting for your payment confirmation</h1>
        <p>
          {checks < MAX_STATUS_CHECKS
            ? "This usually takes a few seconds..."
            : "If you completed the payment, you will receive a confirmation email shortly. If you cancelled, no money was taken."}
        </p>
        <Link to="/" className="bookingBackLink">
          Back to cars
        </Link>
      </section>
    </main>
  );
}

export default PaymentResult;
